import { useState, useEffect } from 'react';
import { Input } from '@/components/cms/ui/input';
import { toast } from 'sonner';
import { ImageCropper } from '@/components/cms/ui/image-cropper';
import { useImageUpload } from '@/hooks/useImageUpload';
import LureoButton from '@/common/LureoButton';
import { TrashIcon, UploadSimpleIcon, CropIcon } from '@phosphor-icons/react';
import { cn } from '@/utils/utils';
import { FOCUS } from '@/components/ui/recipes';
import { PILL_BASE, TAG_PILL } from '@/components/cms/ui/pill-styles';
import { 
  UIImageCategory, 
  UI_IMAGE_CATEGORIES,
  StandardCropData
} from '@/modules/uiImages/types';
import { IconButton } from '@/components/ui/icon-button';
import { shownUrl } from '@/utils/localCopies';

// Helper function to convert data URL to blob
const dataURLToBlob = (dataURL: string): Blob => {
  const arr = dataURL.split(',');
  const mime = arr[0].match(/:(.*?);/)?.[1] || 'image/png';
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }
  return new Blob([u8arr], { type: mime });
};

interface UIImageUploadProps {
  onImageSelect: (imageUrl: string) => void;
  onCropApplied?: (data: { 
    imageOriginalUrl: string; 
    imageCroppedUrl: string; 
    crop: StandardCropData 
  }) => void;
  currentImage?: string;
  currentOriginalImage?: string;
  className?: string;
  buttonText?: string;
  accept?: string;
  maxSizeInMB?: number;
  enableCrop?: boolean;
  imageCategory: UIImageCategory;
  projectInstanceId?: string;
  sceneOriginId?: string;
}

export function UIImageUpload({
  onImageSelect,
  onCropApplied,
  currentImage,
  currentOriginalImage,
  className = '',
  buttonText = 'Upload Image',
  accept = 'image/jpeg,image/jpg,image/png',
  maxSizeInMB = 20,
  enableCrop = true,
  imageCategory,
  projectInstanceId,
  sceneOriginId
}: UIImageUploadProps) {
  const [dragActive, setDragActive] = useState(false);
  const [isEditingExisting, setIsEditingExisting] = useState(false);

  // Use the new centralized upload hook
  const {
    fileInputRef,
    isUploading,
    isCropping,
    tempImage,
    setCropping,
    reset: resetUpload,
    handleFileSelect,
    uploadFile,
    handleCropComplete: completeUploadCrop,
    handleCropCancel: cancelUploadCrop,
  } = useImageUpload(imageCategory, projectInstanceId || sceneOriginId, {
    maxSizeInMB,
    enableCrop,
    autoUpload: false
  });

  const categoryConfig = UI_IMAGE_CATEGORIES[imageCategory];

  // Cleanup temp image URLs
  useEffect(() => {
    return () => {
      if (tempImage && tempImage.startsWith('blob:')) {
        try { URL.revokeObjectURL(tempImage); } catch { /* revoking is best-effort cleanup */ }
      }
    };
  }, [tempImage]);

  const handleInputChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      await handleFileChange(file);
    }
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    const file = e.dataTransfer.files?.[0];
    if (file) {
      await handleFileChange(file);
    }
  };

  const handleButtonClick = () => {
    fileInputRef.current?.click();
  };

  const handleEditExistingImage = () => {
    setIsEditingExisting(true);
    setCropping(true);
    // The ImageCropper will handle the image source logic
    // We don't need to set tempImage here since we're using currentOriginalImage directly
  };

  const handleRemoveImage = () => {
    resetUpload();
    onImageSelect('');
    toast.success('Image removed');
  };


  const handleFileChange = async (file: File) => {
    const result = await handleFileSelect(file);
    
    if (result && result.success) {
      // Auto-upload was successful
      console.log('Auto-upload successful, setting image URL');
      onImageSelect(result.originalUrl || '');
      if (result.croppedUrl && onCropApplied) {
        onCropApplied({
          imageOriginalUrl: result.originalUrl || '',
          imageCroppedUrl: result.croppedUrl,
          crop: result.crop || { x: 0, y: 0, width: 0, height: 0 }
        });
      }
    } else if (result && !result.success) {
      console.log('Upload failed:', result.error);
      toast.error(result.error || 'Upload failed');
    }
    // If result is null, it means cropping is enabled and we're waiting for crop completion
  };


  // Removed handleAutoUpload - now handled by the useImageUpload hook

  const handleCropComplete = async (croppedImageUrl: string, cropData: StandardCropData): Promise<void> => {
    if (isEditingExisting) {
      // For existing images, we need to upload the cropped image to get a storage URL
      // Create a temporary file from the cropped data URL to upload
      const croppedBlob = dataURLToBlob(croppedImageUrl);
      const tempFile = new File([croppedBlob], 'cropped-image.png', { type: 'image/png' });
      
      // Use the upload hook to handle the upload process
      const result = await uploadFile(tempFile, croppedImageUrl, cropData);
      
      if (result.success) {
        if (onCropApplied) {
          onCropApplied({ 
            imageOriginalUrl: currentOriginalImage || currentImage || '',
            imageCroppedUrl: result.croppedUrl || '', // This will now be a Supabase storage URL
            crop: result.crop || { x: 0, y: 0, width: 0, height: 0 }
          });
        } else {
          onImageSelect(result.croppedUrl || result.originalUrl || '');
        }
        toast.success('Image cropped and uploaded successfully');
      } else {
        toast.error(result.error || 'Failed to upload cropped image');
      }
    } else {
      // For new images, use the normal flow
      const result = await completeUploadCrop(croppedImageUrl, cropData);
      
      if (result.success) {
        if (onCropApplied) {
          onCropApplied({ 
            imageOriginalUrl: result.originalUrl || '',
            imageCroppedUrl: result.croppedUrl || '',
            crop: result.crop || { x: 0, y: 0, width: 0, height: 0 }
          });
        } else {
          onImageSelect(result.croppedUrl || result.originalUrl || '');
        }
        toast.success('Image uploaded and cropped');
      } else {
        toast.error(result.error || 'Failed to save image');
      }
    }
    
    // Reset editing state
    setIsEditingExisting(false);
  };

  const handleCropCancel = () => {
    cancelUploadCrop();
    setIsEditingExisting(false);
  };

  const getAspectRatioClass = () => {
    switch (categoryConfig.aspectRatio) {
      case '9:16':
        return 'aspect-[9/16]';
      case '16:9':
        return 'aspect-video';
      case '1:1':
        return 'aspect-square';
      default:
        return 'aspect-video';
    }
  };

  return (
    <div className={`cursor-default select-none ${className}`}>
      <Input
        ref={fileInputRef}
        type="file"
        accept={accept}
        onChange={handleInputChange}
        className="hidden"
        disabled={isUploading}
      />

      <div className="w-full">
        <div className="mt-4 min-h-48 max-h-[60vh]">
          <div
            className={`h-full flex flex-col rounded-lureo-radius-lg transition-all duration-lureo-duration-moderate ${
              dragActive ? 'border-lureo-ink-strong bg-lureo-ink-strong/5' : 'border-lureo-line'
            }`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          >
            <div className="flex-1 relative p-4 flex items-center justify-center min-h-0 max-h-[calc(60vh-80px)] p-2">
              {isCropping && enableCrop ? (
                <div className="w-full h-full flex items-center justify-center relative">
                  <ImageCropper
                    imageSrc={(() => {
                      // For editing existing images, always use the original image (this tab's own copy of one it uploaded)
                      if (isEditingExisting && currentOriginalImage) {
                        return shownUrl(currentOriginalImage);
                      }
                      // For new uploads, use the temp image from the hook
                      if (tempImage) {
                        return tempImage;
                      }
                      // Fallback to current image
                      return shownUrl(currentImage) || '';
                    })()}
                    imageCategory={imageCategory}
                    onCropComplete={handleCropComplete}
                    onCancel={handleCropCancel}
                    className="w-full h-full"
                  />
                  {/* Delete button for editing existing images */}
                  {isEditingExisting && currentImage && (
                    <div className="absolute top-2 right-2 z-20">
                      <IconButton label="Remove image" tone="danger" onClick={handleRemoveImage} disabled={isUploading}>
                        <TrashIcon className="h-4 w-4" />
                      </IconButton>
                    </div>
                  )}
                </div>
              ) : (currentImage && currentImage.trim() !== '') ? (
                <div className="w-full h-full flex items-center justify-center">
                  <div className="relative group w-full max-w-sm flex justify-center">
                    <div className={`relative overflow-hidden border-lureo-border-width border-lureo-line ${getAspectRatioClass()} rounded-lureo-radius-lg bg-lureo-neutral-subtle max-h-64`}>
                      <img
                        src={shownUrl(currentImage)}
                        alt="Uploaded"
                        className="w-full h-full object-contain"
                        style={{ maxHeight: "100%", maxWidth: "100%" }}
                      />
                    </div>
                    <div className="absolute right-2 top-2 z-20 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity duration-lureo-duration-moderate">
                      <IconButton label="Remove image" tone="danger" onClick={handleRemoveImage} disabled={isUploading}>
                        <TrashIcon className="h-4 w-4" />
                      </IconButton>
                    </div>
                  </div>
                </div>
              ) : (
                <button
                  type="button"
                  className={cn(`group relative flex ${getAspectRatioClass()} w-full max-w-sm max-h-[45vh] flex-col items-center justify-center rounded-lureo-radius-lg border-lureo-border-width-heavy border-dashed border-lureo-brand-ink transition-all bg-transparent p-2 ${
                    dragActive ? 'before:opacity-20' : 'hover:before:opacity-10'
                  } cursor-pointer
                  before:absolute before:inset-0 before:rounded-[inherit] before:opacity-0
                  before:bg-lureo-brand-ink before:transition-opacity
                  [&>*]:relative [&>*]:z-10`, FOCUS)}
                  onClick={handleButtonClick}
                >
                  <span className="flex flex-col items-center">
                    <UploadSimpleIcon className="mb-4 h-12 w-12 text-lureo-brand-ink" />
                    <span className="flex flex-col items-center">
                      <span className="mb-2 text-lureo-18 text-lureo-brand-ink leading-7">Drag and Drop files</span>
                      <span className="flex gap-4">
                        <span className={cn(PILL_BASE, TAG_PILL)}>JPG</span>
                        <span className={cn(PILL_BASE, TAG_PILL)}>PNG</span>
                      </span>
                      <span className="mt-2 text-lureo-12 text-lureo-ink-subtle leading-4">Max: {maxSizeInMB}MB / file</span>
                      <span className="mt-2 text-lureo-12 text-lureo-ink-subtle leading-4">Required: {categoryConfig.aspectRatio}</span>
                    </span>
                  </span>
                </button>
              )}
            </div>

            <div className="mt-auto p-4 pt-0">
              {!isCropping && (currentImage && currentImage.trim() !== '') ? (
                <div className="flex gap-2 justify-center">
                  <LureoButton
                    variant="secondary"
                    onClick={handleEditExistingImage}
                    disabled={isUploading}
                    className="h-9 rounded-lureo-radius-button-pill px-4"
                  >
                    <CropIcon className="h-5 w-5" />
                    {isUploading ? 'Uploading...' : 'Edit Image'}
                  </LureoButton>
                </div>
              ) : !isCropping ? (
                <div className="flex justify-center">
                  <LureoButton
                    variant="secondary"
                    onClick={handleButtonClick}
                    disabled={isUploading}
                    className="h-12 rounded-lureo-radius-button-pill px-6"
                  >
                    <UploadSimpleIcon className="h-5 w-5" />
                    {isUploading ? 'Uploading...' : buttonText}
                  </LureoButton>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
